<template>
  <div class="member-stamp-panel">
    <button type="button" class="stamp-open-btn" @click="openModal">
      <v-icon size="14">mdi-stamper</v-icon>
      결재란정보
    </button>

    <v-dialog v-model="showModal" max-width="900" persistent content-class="member-stamp-dialog">
      <div class="stamp-modal-card">
        <LoadingOverlay :show="loading" text="불러오는 중..." />
        <LoadingOverlay :show="saving" text="저장 중..." variant="save" />
        <LoadingOverlay :show="uploading" text="업로드 중..." />
        <LoadingOverlay :show="deletingType !== ''" text="삭제 중..." variant="delete" />

        <div class="stamp-modal-header">
          <div class="header-icon">
            <v-icon size="20" color="white">mdi-stamper</v-icon>
          </div>
          <div class="header-text">
            <h3>결재란 정보</h3>
            <p>결의서 인명 · 도장 이미지</p>
          </div>
          <div class="header-spacer"></div>
          <button type="button" class="close-btn" @click="closeModal">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </div>

        <div class="stamp-modal-body">
          <div class="stamp-grid">
            <div
              v-for="stampType in stampTypes"
              :key="stampType.key"
              class="stamp-card"
              :class="{ 'has-image': stampImages[stampType.key] }"
            >
              <div class="stamp-image-area" @click="openFileSelector(stampType.key)">
                <img
                  v-if="stampImages[stampType.key]"
                  :src="stampImages[stampType.key]"
                  :alt="stampType.label"
                  class="stamp-img"
                />
                <div v-else class="stamp-empty">
                  <v-icon size="32" color="#94a3b8">mdi-stamper</v-icon>
                  <span>{{ stampType.label }}</span>
                </div>
                <div v-if="stampImages[stampType.key]" class="stamp-overlay">
                  <v-icon size="20" color="white">mdi-pencil</v-icon>
                </div>
              </div>
              <div class="stamp-label">{{ stampType.label }}</div>
              <div class="stamp-actions">
                <v-btn
                  v-if="stampImages[stampType.key]"
                  variant="flat"
                  class="btn-delete"
                  size="small"
                  block
                  :disabled="deletingType === stampType.key"
                  @click="handleDeleteStamp(stampType.key)"
                >
                  삭제
                </v-btn>
                <v-btn
                  v-else
                  variant="flat"
                  class="btn-confirm"
                  size="small"
                  block
                  @click="openFileSelector(stampType.key)"
                >
                  추가
                </v-btn>
              </div>
            </div>
          </div>

          <div class="stamp-names">
            <div class="names-title">결재자 이름</div>
            <div class="names-grid">
              <div class="name-field">
                <label>시설장</label>
                <input v-model="memberInfo.chief_name" type="text" placeholder="시설장" />
              </div>
              <div class="name-field">
                <label>담당자</label>
                <input v-model="memberInfo.paym_name" type="text" placeholder="담당자" />
              </div>
              <div class="name-field">
                <label>수입원</label>
                <input v-model="memberInfo.income_name" type="text" placeholder="수입원" />
              </div>
              <div class="name-field">
                <label>지출원</label>
                <input v-model="memberInfo.outcome_name" type="text" placeholder="지출원" />
              </div>
            </div>
          </div>
        </div>

        <div class="stamp-modal-footer">
          <v-btn variant="text" class="btn-cancel" @click="closeModal">취소</v-btn>
          <v-spacer />
          <v-btn variant="flat" class="btn-save" :disabled="saving" @click="saveMemberInfo">
            <v-icon v-if="saving" class="mdi-spin" size="16">mdi-loading</v-icon>
            저장
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <input
      ref="fileInputRef"
      type="file"
      accept="image/png,image/jpeg,image/jpg"
      class="hidden-file-input"
      @change="onFileSelected"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onBeforeUnmount } from 'vue';
import { bankbookApi } from '@/services/api';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';

const props = defineProps<{
  memberCode: string;
}>();

const stampTypes = [
  { key: 'chief', label: '시설장' },
  { key: 'paym', label: '담당자' },
  { key: 'income', label: '수입원' },
  { key: 'outcome', label: '지출원' },
] as const;

type StampTypeKey = typeof stampTypes[number]['key'];

const showModal = ref(false);
const loading = ref(false);
const saving = ref(false);
const uploading = ref(false);
const deletingType = ref('');
const currentStampType = ref<StampTypeKey | ''>('');
const fileInputRef = ref<HTMLInputElement | null>(null);

const memberInfo = reactive({
  chief_name: '',
  paym_name: '',
  income_name: '',
  outcome_name: '',
});

const stampImages = ref<Record<string, string>>({});
const stampBlobUrls: string[] = [];

const revokeStampUrls = () => {
  stampBlobUrls.forEach((url) => URL.revokeObjectURL(url));
  stampBlobUrls.length = 0;
  stampImages.value = {};
};

const loadStampImage = async (stampType: StampTypeKey) => {
  try {
    const response = await bankbookApi.getStampImage(props.memberCode, stampType);
    const url = URL.createObjectURL(response.data);
    stampBlobUrls.push(url);
    stampImages.value[stampType] = url;
  } catch {
    delete stampImages.value[stampType];
  }
};

const loadAllData = async () => {
  if (!props.memberCode) return;
  loading.value = true;
  revokeStampUrls();

  try {
    const financeRes = await bankbookApi.getFinanceInfo(props.memberCode);
    const data = financeRes.data?.data;
    if (data) {
      memberInfo.chief_name = data.chief_name || '';
      memberInfo.paym_name = data.paym_name || '';
      memberInfo.income_name = data.income_name || '';
      memberInfo.outcome_name = data.outcome_name || '';
    }

    const listRes = await bankbookApi.getStampList(props.memberCode);
    const stamps = listRes.data?.data || {};
    const types = Object.keys(stamps).filter((k) => stamps[k]);
    await Promise.all(types.map((type) => loadStampImage(type as StampTypeKey)));
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`결재란 정보 불러오기 실패: ${message}`);
  } finally {
    loading.value = false;
  }
};

const openModal = async () => {
  showModal.value = true;
  await loadAllData();
};

const closeModal = () => {
  showModal.value = false;
};

const openFileSelector = (stampType: StampTypeKey) => {
  currentStampType.value = stampType;
  fileInputRef.value?.click();
};

const onFileSelected = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file || !currentStampType.value) return;

  uploading.value = true;
  try {
    await bankbookApi.uploadStamp(props.memberCode, currentStampType.value, file);
    if (stampImages.value[currentStampType.value]) {
      URL.revokeObjectURL(stampImages.value[currentStampType.value]);
      const idx = stampBlobUrls.indexOf(stampImages.value[currentStampType.value]);
      if (idx >= 0) stampBlobUrls.splice(idx, 1);
    }
    await loadStampImage(currentStampType.value);
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`도장 업로드 실패: ${message}`);
  } finally {
    uploading.value = false;
    currentStampType.value = '';
  }
};

const handleDeleteStamp = async (stampType: StampTypeKey) => {
  if (!confirm(`${stampTypes.find((t) => t.key === stampType)?.label} 도장을 삭제하시겠습니까?`)) return;

  deletingType.value = stampType;
  try {
    await bankbookApi.deleteStamp(props.memberCode, stampType);
    if (stampImages.value[stampType]) {
      URL.revokeObjectURL(stampImages.value[stampType]);
      const idx = stampBlobUrls.indexOf(stampImages.value[stampType]);
      if (idx >= 0) stampBlobUrls.splice(idx, 1);
      delete stampImages.value[stampType];
    }
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`도장 삭제 실패: ${message}`);
  } finally {
    deletingType.value = '';
  }
};

const saveMemberInfo = async () => {
  saving.value = true;
  try {
    await bankbookApi.upsertFinanceInfo(props.memberCode, { ...memberInfo });
    alert('결재자 정보가 저장되었습니다.');
    closeModal();
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`결재자 정보 저장 실패: ${message}`);
  } finally {
    saving.value = false;
  }
};

onBeforeUnmount(() => {
  revokeStampUrls();
});
</script>

<style lang="scss">
@import '@/assets/styles/variables';
@import '@/assets/styles/pages/bankbook-reports';
</style>
